import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import GlassNav from "@/components/GlassNav";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Download, Image as ImageIcon, Heart, Video, ExternalLink, QrCode, Palette, ListFilter, Trash2, Check, X, Mic } from "lucide-react";

const FONTS = ["Cormorant Garamond", "Playfair Display", "DM Sans", "Outfit", "Great Vibes"];

export default function WeddingAdmin() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("customize");
  const [w, setW] = useState(null);
  const [stats, setStats] = useState(null);
  const [memories, setMemories] = useState([]);
  const [filter, setFilter] = useState({ status: "", media_type: "", with_wishes: "" });

  const loadAll = async () => {
    const [wr, sr] = await Promise.all([ api.get(`/weddings/${id}`), api.get(`/weddings/${id}/stats`) ]);
    setW(wr.data); setStats(sr.data);
  };

  const loadMemories = async () => {
    const params = {};
    if (filter.status) params.status = filter.status;
    if (filter.media_type) params.media_type = filter.media_type;
    if (filter.with_wishes === "yes") params.with_wishes = true;
    if (filter.with_wishes === "no") params.with_wishes = false;
    const r = await api.get(`/weddings/${id}/memories`, { params });
    setMemories(r.data);
  };

  useEffect(() => { loadAll(); }, [id]);
  useEffect(() => { if (tab === "memories" || tab === "wishes") loadMemories(); }, [tab, filter]);

  if (!w) return <div className="min-h-screen"><GlassNav /><div className="pt-40 text-center text-neutral-500">Loading...</div></div>;

  const guestUrl = `${window.location.origin}/w/${w.slug}`;
  const qrSrc = `${BACKEND_URL}/api/public/weddings/${w.slug}/qr?target_url=${encodeURIComponent(guestUrl)}&t=${Date.now()}`;

  const saveWedding = async (patch) => {
    const r = await api.patch(`/weddings/${id}`, patch);
    setW(r.data);
    toast.success("Saved");
  };

  const setTheme = (patch) => saveWedding({ theme: { ...w.theme, ...patch } });

  const moderate = async (mid, status) => {
    await api.patch(`/weddings/${id}/memories/${mid}`, { status });
    toast.success(status);
    loadMemories(); loadAll();
  };
  const del = async (mid) => {
    if (!confirm("Delete this memory?")) return;
    await api.delete(`/weddings/${id}/memories/${mid}`);
    toast.success("Deleted");
    loadMemories(); loadAll();
  };

  return (
    <div className="min-h-screen relative">
      <GlassNav />
      <div className="pt-28 pb-24 px-4 max-w-6xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <Link to="/dashboard" className="text-xs text-neutral-500 hover:underline" data-testid="back-dashboard">← All weddings</Link>
            <h1 className="font-serif text-4xl sm:text-5xl mt-1">{w.display_name}</h1>
            <div className="text-neutral-600 text-sm">{w.date} · {w.venue_name || "—"}</div>
          </div>
          <a href={guestUrl} target="_blank" rel="noreferrer" className="btn-ghost-glass rounded-full px-5 py-2 text-sm inline-flex items-center gap-2" data-testid="open-guest-page">
            Open guest page <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        <div className="grid sm:grid-cols-4 gap-3 mb-6">
          <Stat label="Memories" value={stats?.memories||0} icon={<ImageIcon className="w-4 h-4" />} testid="stat-memories" />
          <Stat label="Photos" value={stats?.photos||0} icon={<ImageIcon className="w-4 h-4" />} testid="stat-photos" />
          <Stat label="Videos" value={stats?.videos||0} icon={<Video className="w-4 h-4" />} testid="stat-videos" />
          <Stat label="Wishes" value={stats?.wishes||0} icon={<Heart className="w-4 h-4" />} testid="stat-wishes" />
        </div>

        <div className="glass p-2 inline-flex flex-wrap gap-1 mb-4">
          {[
            ["customize","Customize", <Palette className="w-4 h-4" key="a"/>],
            ["theme","Theme", <Palette className="w-4 h-4" key="b"/>],
            ["memories","Memories", <ImageIcon className="w-4 h-4" key="c"/>],
            ["wishes","Wishes", <Heart className="w-4 h-4" key="d"/>],
            ["qr","QR Card", <QrCode className="w-4 h-4" key="e"/>],
          ].map(([k,l,ic]) => (
            <button key={k} onClick={()=>setTab(k)}
                    className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm ${tab===k?"btn-accent":""}`}
                    data-testid={`tab-${k}`}>
              {ic}{l}
            </button>
          ))}
        </div>

        {tab === "customize" && (
          <CustomizeTab w={w} save={saveWedding} />
        )}
        {tab === "theme" && (
          <ThemeTab w={w} setTheme={setTheme} save={saveWedding} />
        )}
        {tab === "memories" && (
          <MemoriesTab memories={memories} filter={filter} setFilter={setFilter} moderate={moderate} del={del} />
        )}
        {tab === "wishes" && (
          <WishesTab memories={memories.filter(m=>m.wish_text)} moderate={moderate} del={del} />
        )}
        {tab === "qr" && (
          <QRTab qrSrc={qrSrc} guestUrl={guestUrl} w={w} save={saveWedding} />
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon, testid }) {
  return (
    <div className="glass p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500">{icon} {label}</div>
      <div className="font-serif text-3xl mt-1">{value}</div>
    </div>
  );
}

function LabeledInput({ label, ...rest }) {
  return (
    <label className="block">
      <div className="text-xs text-neutral-500 uppercase tracking-widest mb-1">{label}</div>
      <input className="glass-input rounded-full px-5 py-3 text-sm w-full" {...rest} />
    </label>
  );
}

function CustomizeTab({ w, save }) {
  const [f, setF] = useState(w);
  useEffect(()=>setF(w),[w]);
  const set = (k,v)=>setF(x=>({...x,[k]:v}));
  return (
    <div className="glass p-6 grid md:grid-cols-2 gap-4 fade-in">
      <LabeledInput label="Partner 1" value={f.partner_1||""} onChange={e=>set("partner_1",e.target.value)} data-testid="cust-p1" />
      <LabeledInput label="Partner 2" value={f.partner_2||""} onChange={e=>set("partner_2",e.target.value)} data-testid="cust-p2" />
      <LabeledInput label="Wedding date" type="date" value={f.date||""} onChange={e=>set("date",e.target.value)} data-testid="cust-date" />
      <LabeledInput label="Wedding time" type="time" value={f.time||""} onChange={e=>set("time",e.target.value)} data-testid="cust-time" />
      <LabeledInput label="Venue name" value={f.venue_name||""} onChange={e=>set("venue_name",e.target.value)} data-testid="cust-venue" />
      <LabeledInput label="City" value={f.city||""} onChange={e=>set("city",e.target.value)} data-testid="cust-city" />
      <LabeledInput label="URL slug" value={f.slug||""} onChange={e=>set("slug",e.target.value)} data-testid="cust-slug" />
      <LabeledInput label="Cover image URL" value={f.cover_image||""} onChange={e=>set("cover_image",e.target.value)} data-testid="cust-cover" />
      <LabeledInput label="Monogram" value={f.monogram||""} onChange={e=>set("monogram",e.target.value)} data-testid="cust-monogram" />
      <LabeledInput label="Upload button text" value={f.upload_button_text||""} onChange={e=>set("upload_button_text",e.target.value)} data-testid="cust-uploadbtn" />
      <LabeledInput label="Gallery button text" value={f.gallery_button_text||""} onChange={e=>set("gallery_button_text",e.target.value)} data-testid="cust-gallerybtn" />
      <LabeledInput label="Wish prompt" value={f.wish_prompt||""} onChange={e=>set("wish_prompt",e.target.value)} data-testid="cust-wishprompt" />
      <label className="block md:col-span-2">
        <div className="text-xs text-neutral-500 uppercase tracking-widest mb-1">Welcome message</div>
        <textarea rows={3} className="glass-input rounded-2xl px-5 py-3 text-sm w-full" value={f.welcome_message||""} onChange={e=>set("welcome_message",e.target.value)} data-testid="cust-welcome" />
      </label>
      <label className="flex items-center gap-2 text-sm md:col-span-2">
        <input type="checkbox" checked={!!f.moderation_enabled} onChange={e=>set("moderation_enabled",e.target.checked)} data-testid="cust-moderation" />
        Enable moderation (photos require approval)
      </label>
      <div className="md:col-span-2 flex justify-end">
        <button onClick={()=>save(f)} className="btn-accent rounded-full px-6 py-2 text-sm" data-testid="cust-save">Save changes</button>
      </div>
    </div>
  );
}

function ThemeTab({ w, setTheme }) {
  const t = w.theme || {};
  return (
    <div className="glass p-6 grid md:grid-cols-2 gap-6 fade-in" data-testid="theme-panel">
      <div className="space-y-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Accent color</div>
          <input type="color" value={t.accent_color||"#C7A76C"} onChange={e=>setTheme({accent_color:e.target.value})} className="w-16 h-10 rounded-lg" data-testid="theme-accent" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Background color</div>
          <input type="color" value={t.background_color||"#F5F6F8"} onChange={e=>setTheme({background_color:e.target.value})} className="w-16 h-10 rounded-lg" data-testid="theme-bg" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Glass opacity ({(t.glass_opacity||0.55).toFixed(2)})</div>
          <input type="range" min="0.3" max="0.85" step="0.05" value={t.glass_opacity||0.55} onChange={e=>setTheme({glass_opacity:parseFloat(e.target.value)})} className="w-full" data-testid="theme-opacity" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Blur intensity ({t.glass_blur||24}px)</div>
          <input type="range" min="8" max="40" step="2" value={t.glass_blur||24} onChange={e=>setTheme({glass_blur:parseInt(e.target.value)})} className="w-full" data-testid="theme-blur" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Corner radius ({t.corner_radius||24}px)</div>
          <input type="range" min="8" max="40" step="2" value={t.corner_radius||24} onChange={e=>setTheme({corner_radius:parseInt(e.target.value)})} className="w-full" data-testid="theme-radius" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Heading font</div>
            <select className="glass-input rounded-full px-4 py-2 text-sm w-full" value={t.heading_font||"Cormorant Garamond"} onChange={e=>setTheme({heading_font:e.target.value})} data-testid="theme-hfont">
              {FONTS.map(f=><option key={f}>{f}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Body font</div>
            <select className="glass-input rounded-full px-4 py-2 text-sm w-full" value={t.body_font||"DM Sans"} onChange={e=>setTheme({body_font:e.target.value})} data-testid="theme-bfont">
              {FONTS.map(f=><option key={f}>{f}</option>)}
            </select>
          </label>
        </div>
      </div>
      <div className="rounded-3xl p-6 relative overflow-hidden" style={{ background: t.background_color||"#F5F6F8" }}>
        <div className="glass p-6 gloss-highlight" style={{ borderRadius: (t.corner_radius||24)+"px", background: `rgba(255,255,255,${t.glass_opacity||0.55})`, backdropFilter: `blur(${t.glass_blur||24}px)` }}>
          <div className="text-xs uppercase tracking-widest text-neutral-500">Preview</div>
          <div style={{ fontFamily: `'${t.heading_font||"Cormorant Garamond"}', serif` }} className="text-4xl mt-2">{w.display_name}</div>
          <div style={{ fontFamily: `'${t.body_font||"DM Sans"}', sans-serif` }} className="text-sm text-neutral-600 mt-2">{w.welcome_message}</div>
          <button className="rounded-full px-5 py-2 text-sm mt-4" style={{ background: t.accent_color||"#C7A76C", color: "#fff" }}>{w.upload_button_text}</button>
        </div>
      </div>
    </div>
  );
}

function MemoriesTab({ memories, filter, setFilter, moderate, del }) {
  return (
    <div className="fade-in">
      <div className="glass p-4 mb-4 flex flex-wrap gap-2 items-center" data-testid="mem-filters">
        <ListFilter className="w-4 h-4 text-neutral-500" />
        <select value={filter.status} onChange={e=>setFilter({...filter,status:e.target.value})} className="glass-input rounded-full px-4 py-2 text-sm" data-testid="filter-status">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <select value={filter.media_type} onChange={e=>setFilter({...filter,media_type:e.target.value})} className="glass-input rounded-full px-4 py-2 text-sm" data-testid="filter-type">
          <option value="">All media</option>
          <option value="image">Photos</option>
          <option value="video">Videos</option>
        </select>
        <select value={filter.with_wishes} onChange={e=>setFilter({...filter,with_wishes:e.target.value})} className="glass-input rounded-full px-4 py-2 text-sm" data-testid="filter-wishes">
          <option value="">All wishes</option>
          <option value="yes">With wishes</option>
          <option value="no">Without wishes</option>
        </select>
      </div>
      {memories.length === 0 ? (
        <div className="glass p-10 text-center text-neutral-500">No memories yet.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {memories.map(m => (
            <div key={m.id} className="glass overflow-hidden" data-testid={`mem-${m.id}`}>
              <div className="aspect-square bg-neutral-200 relative">
                {m.media_type === "video" ? (
                  <>
                    {m.thumbnail_url && <img src={`${BACKEND_URL}${m.thumbnail_url}`} alt="" className="w-full h-full object-cover" />}
                    <div className="absolute inset-0 grid place-items-center bg-black/20"><Video className="w-8 h-8 text-white" /></div>
                  </>
                ) : (
                  <img src={`${BACKEND_URL}${m.media_url}`} alt="" className="w-full h-full object-cover" />
                )}
                <div className="absolute top-2 right-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded-full bg-white/80">{m.status}</div>
              </div>
              <div className="p-3">
                <div className="text-xs text-neutral-500 flex items-center gap-2">
                  {m.guest_name}
                  {m.voice_url && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest" title="Voice message"><Mic className="w-3 h-3" /> Voice</span>}
                </div>
                {m.wish_text && <div className="font-serif text-sm mt-1 line-clamp-3">"{m.wish_text}"</div>}
                {m.voice_url && (
                  <audio src={`${BACKEND_URL}${m.voice_url}`} controls className="w-full mt-2 h-8" />
                )}
                <div className="flex gap-1 mt-2">
                  <button onClick={()=>moderate(m.id,"approved")} className="btn-ghost-glass rounded-full p-2" data-testid={`mem-approve-${m.id}`}><Check className="w-3 h-3" /></button>
                  <button onClick={()=>moderate(m.id,"rejected")} className="btn-ghost-glass rounded-full p-2" data-testid={`mem-reject-${m.id}`}><X className="w-3 h-3" /></button>
                  <button onClick={()=>del(m.id)} className="btn-ghost-glass rounded-full p-2" data-testid={`mem-delete-${m.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WishesTab({ memories, moderate, del }) {
  return (
    <div className="fade-in">
      {memories.length === 0 ? (
        <div className="glass p-10 text-center text-neutral-500">No wishes yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {memories.map(m => (
            <div key={m.id} className="glass p-4 flex gap-4" data-testid={`wish-${m.id}`}>
              <div className="w-24 h-24 bg-neutral-200 rounded-2xl overflow-hidden flex-shrink-0">
                <img src={`${BACKEND_URL}${m.thumbnail_url || m.media_url}`} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-neutral-500 flex items-center gap-2">
                  {m.guest_name}
                  {m.voice_url && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest"><Mic className="w-3 h-3" /> Voice</span>}
                </div>
                <div className="font-serif text-lg mt-1">"{m.wish_text}"</div>
                {m.voice_url && (
                  <audio src={`${BACKEND_URL}${m.voice_url}`} controls className="w-full mt-2 h-8" />
                )}
                <div className="flex gap-1 mt-2">
                  <button onClick={()=>moderate(m.id,"approved")} className="btn-ghost-glass rounded-full p-2" data-testid={`wish-approve-${m.id}`}><Check className="w-3 h-3" /></button>
                  <button onClick={()=>del(m.id)} className="btn-ghost-glass rounded-full p-2" data-testid={`wish-delete-${m.id}`}><Trash2 className="w-3 h-3" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QRTab({ qrSrc, guestUrl, w, save }) {
  const [heading, setHeading] = useState("Share Your Memories");
  const [subtitle, setSubtitle] = useState("Scan to upload your photos and wishes.");

  return (
    <div className="grid md:grid-cols-2 gap-4 fade-in">
      <div className="glass p-6" data-testid="qr-editor">
        <div className="font-serif text-2xl mb-3">Card content</div>
        <LabeledInput label="Heading" value={heading} onChange={e=>setHeading(e.target.value)} data-testid="qr-heading" />
        <div className="h-2" />
        <label className="block">
          <div className="text-xs text-neutral-500 uppercase tracking-widest mb-1">Subtitle</div>
          <textarea rows={2} className="glass-input rounded-2xl px-5 py-3 text-sm w-full" value={subtitle} onChange={e=>setSubtitle(e.target.value)} data-testid="qr-subtitle" />
        </label>
        <div className="mt-4 text-xs text-neutral-500 uppercase tracking-widest">Guest link</div>
        <div className="text-sm break-all">{guestUrl}</div>
        <div className="flex gap-2 mt-4">
          <a href={qrSrc} download={`${w.slug}-qr.png`} className="btn-accent rounded-full px-4 py-2 text-sm inline-flex items-center gap-2" data-testid="qr-download">
            <Download className="w-4 h-4" /> Download QR
          </a>
          <button onClick={()=>{navigator.clipboard.writeText(guestUrl); toast.success("Link copied");}} className="btn-ghost-glass rounded-full px-4 py-2 text-sm" data-testid="qr-copy">
            Copy link
          </button>
        </div>
      </div>
      <div className="glass p-8 text-center" data-testid="qr-preview" style={{ background: `rgba(255,255,255,${w.theme?.glass_opacity||0.6})` }}>
        <div className="text-xs uppercase tracking-widest text-neutral-500">You're invited</div>
        <div className="font-serif text-3xl mt-1">{w.display_name}</div>
        <div className="text-neutral-600 text-sm mt-1">{w.date}</div>
        <div className="mt-6 inline-block p-4 rounded-3xl bg-white shadow-inner">
          <img src={qrSrc} alt="QR code" className="w-56 h-56" />
        </div>
        <div className="font-serif text-2xl mt-6">{heading}</div>
        <div className="text-neutral-600 text-sm mt-1">{subtitle}</div>
        <div className="mt-4 font-script text-3xl" style={{ color: w.theme?.accent_color||"#C7A76C" }}>{w.monogram}</div>
      </div>
    </div>
  );
}
